import { BaseService } from "../../core/baseService";
import {
  GuestGuideServiceInterface,
  UploadGuestGuideParams,
  GuestGuideInfo,
  GuideInfo,
  DigitalGuestGuide,
  DigitalGuideFaq,
  CreateUpdateDigitalGuestGuide,
  GuestGuideTemplate,
  GuideLanguages,
  DigitalGuestGuideTranslation,
  CreateUpdateGuideTranslation,
  AutoTranslateGuideRequest,
} from "../types/guestGuide.types";
import { logger } from "../../../utils/logger";

export class GuestGuideService
  extends BaseService
  implements GuestGuideServiceInterface
{
  async uploadGuestGuide(params: UploadGuestGuideParams) {
    const { file } = params;
    const formData = new FormData();
    formData.append("file", file);

    return this.handleRequest<{ message: string }>(
      this.client.post("/api/admin/guest-guide", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
    );
  }

  async getGuestGuideInfo() {
    const result = await this.handleRequest<GuestGuideInfo>(
      this.client.get("/api/admin/guest-guide"),
    );

    // Handle 404 as successful response with null data
    if (!result.success && result.error.status === 404) {
      return { success: true, data: null } as const;
    }

    return result;
  }

  async deleteGuestGuide() {
    const result = await this.handleRequest<void>(
      this.client.delete("/api/admin/guest-guide"),
    );

    // Handle 404 as successful response (guide already deleted)
    if (!result.success && result.error.status === 404) {
      logger.warn("Attempted to delete guest guide, but it was not found.");
      return { success: true, data: undefined } as const;
    }

    return result;
  }

  async getGuideInfo() {
    const result = await this.handleRequest<GuideInfo>(
      this.client.get("/api/admin/guest-guide/info"),
    );

    // Handle 404 as successful response with null data
    if (!result.success && result.error.status === 404) {
      return { success: true, data: null } as const;
    }

    return result;
  }

  async getDigitalGuide() {
    const result = await this.handleRequest<DigitalGuestGuide>(
      this.client.get("/api/admin/guest-guide/digital"),
    );

    // Handle 404 as successful response with null data
    if (!result.success && result.error.status === 404) {
      return { success: true, data: null } as const;
    }

    return result;
  }

  async createDigitalGuide(data: CreateUpdateDigitalGuestGuide) {
    // Clean up the data - ensure no undefined values
    const cleanData = JSON.parse(JSON.stringify(data));

    // Ensure FAQs don't have undefined ids
    if (cleanData.faqs) {
      cleanData.faqs = cleanData.faqs.map((faq: DigitalGuideFaq) => ({
        ...faq,
        id: faq.id || null, // Convert undefined to null for backend
      }));
    }

    logger.log("Creating digital guide with data:", cleanData);
    const result = await this.handleRequest<DigitalGuestGuide>(
      this.client.post("/api/admin/guest-guide/digital", cleanData),
    );
    if (!result.success) {
      logger.error("Create digital guide failed:", result.error);
    }
    return result;
  }

  async updateDigitalGuide(data: CreateUpdateDigitalGuestGuide) {
    // Clean up the data - ensure no undefined values
    const cleanData = JSON.parse(JSON.stringify(data));

    // Ensure FAQs don't have undefined ids
    if (cleanData.faqs) {
      cleanData.faqs = cleanData.faqs.map((faq: DigitalGuideFaq) => ({
        ...faq,
        id: faq.id || null, // Convert undefined to null for backend
      }));
    }

    logger.log("Updating digital guide with data:", cleanData);
    const result = await this.handleRequest<DigitalGuestGuide>(
      this.client.put("/api/admin/guest-guide/digital", cleanData),
    );
    if (!result.success) {
      logger.error("Update digital guide failed:", result.error);
    }
    return result;
  }

  async deleteDigitalGuide() {
    const result = await this.handleRequest<void>(
      this.client.delete("/api/admin/guest-guide/digital"),
    );

    // Handle 404 as successful response (guide already deleted)
    if (!result.success && result.error.status === 404) {
      logger.warn("Attempted to delete digital guide, but it was not found.");
      return { success: true, data: undefined } as const;
    }

    return result;
  }

  async getTemplates() {
    return this.handleRequest<GuestGuideTemplate[]>(
      this.client.get("/api/admin/guest-guide/templates"),
    );
  }

  // Translation methods

  async getAvailableLanguages() {
    return this.handleRequest<GuideLanguages>(
      this.client.get("/api/admin/guest-guide/languages"),
    );
  }

  async getDigitalGuideByLanguage(languageCode: string) {
    const result = await this.handleRequest<DigitalGuestGuide>(
      this.client.get(`/api/admin/guest-guide/digital/${languageCode}`),
    );

    // Handle 404 as successful response with null data
    if (!result.success && result.error.status === 404) {
      return { success: true, data: null } as const;
    }

    return result;
  }

  async getTranslation(languageCode: string) {
    const result = await this.handleRequest<DigitalGuestGuideTranslation>(
      this.client.get(`/api/admin/guest-guide/translations/${languageCode}`),
    );

    // Handle 404 as successful response with null data
    if (!result.success && result.error.status === 404) {
      return { success: true, data: null } as const;
    }

    return result;
  }

  async saveTranslation(data: CreateUpdateGuideTranslation) {
    // Clean up the data
    const cleanData = JSON.parse(JSON.stringify(data));

    logger.log("Saving translation with data:", cleanData);
    const result = await this.handleRequest<DigitalGuestGuideTranslation>(
      this.client.post("/api/admin/guest-guide/translations", cleanData),
    );

    if (!result.success) {
      logger.error("Save translation failed:", result.error);
    }

    return result;
  }

  async deleteTranslation(languageCode: string) {
    const result = await this.handleRequest<void>(
      this.client.delete(
        `/api/admin/guest-guide/translations/${languageCode}`,
      ),
    );

    // Handle 404 as successful response (translation already deleted)
    if (!result.success && result.error.status === 404) {
      logger.warn("Attempted to delete translation, but it was not found.");
      return { success: true, data: undefined } as const;
    }

    return result;
  }

  async autoTranslate(request: AutoTranslateGuideRequest) {
    logger.log("Auto-translating guide to:", request.targetLanguageCode);
    const result = await this.handleRequest<DigitalGuestGuideTranslation>(
      this.client.post(
        "/api/admin/guest-guide/translations/auto-translate",
        request,
      ),
    );

    if (!result.success) {
      logger.error("Auto-translate failed:", result.error);
    }

    return result;
  }
}
